import {ref} from "vue";
import {defineStore} from "pinia";
import {useRouter, useRoute} from "vue-router";
import axios from "axios";
import {toast} from "vue3-toastify";
import {randomByNumber} from "../js/utils";

import {CommonStore} from "./common-store";
import {OptionsStore} from "./options-store";
import {ModelStore} from "./model-store";
import {LoaderButtonStore} from "./loader-button-store";
import {AdminEstimateSubmit, Estimate, EstimateSubmit, GModalData} from "@/js/interface";
import {Modals} from "@/js/types";
import {ResponseStatusCode} from "@/js/error";
import {EstimatesStore} from "./estimates-store";

/******************************************
 * 하이쓰리디 - 견적서 관리 스토어
 ******************************************/

export const EstimateStore = defineStore("Estimate", () => {
  const modelStore = ModelStore();
  const commonStore = CommonStore();
  const loaderButtonStore = LoaderButtonStore();
  const router = useRouter();

  const isEstimateSubmit = ref(true);

  const estimate = ref({} as Estimate);

  async function createEstimate() {
    estimate.value = {
      id: Number.parseInt(randomByNumber(9)),
      paintCost: 0,
      buildCost: 0,
      postProcessCost: 0,
      shippingCost: 0,
      discountCost: 0,
      totalCost: 0,
      vat: 0,
    };
  }

  async function calculateTotalCost(optionCosts?: {
    buildCost?: number;
    postProcessCost?: number;
    paintCost?: number;
    shippingCost?: number;
  }) {
    const result = await axios.put(`/estimates/${estimate.value.id}/total-cost`, optionCosts);

    estimate.value.buildCost = result.data.result.buildCost;
    estimate.value.discountCost = result.data.result.discountCost;
    estimate.value.totalCost = result.data.result.totalCost;

    if (result.data.result.shippingCost) {
      estimate.value.shippingCost = result.data.result.shippingCost;
    }
    if (result.data.result.postProcessCost) {
      estimate.value.postProcessCost = result.data.result.postProcessCost;
    }
    if (result.data.result.paintCost) {
      estimate.value.paintCost = result.data.result.paintCost;
    }
  }

  function reqCreateEstimate(pEstimate: EstimateSubmit) {
    axios
      .post("/admin/estimates", pEstimate)
      .then((r) => {
        if (!r) {
          loaderButtonStore.stop();
          toast.error("네트워크가 연결이 안되있거나 서버에 연결할 수 없습니다.", {
            transition: toast.TRANSITIONS.SLIDE,
            position: toast.POSITION.TOP_CENTER,
          });
          return;
        }

        if (ResponseStatusCode.Create_Estimate__Not_Found_Option === r.data.status) {
          loaderButtonStore.stop();
          toast.error("배송비용 설정을 찾을 수 없습니다, 관리자에게 문의하세요.", {
            transition: toast.TRANSITIONS.SLIDE,
            position: toast.POSITION.TOP_CENTER,
          });
          return;
        }

        CommonStore().closeModal();

        EstimatesStore().reqEstimates();
      })
      .catch((e) => {
        console.error(e);
        loaderButtonStore.stop();
      });

    return;
  }

  function reqSubmit(pEstimate: EstimateSubmit) {
    if (!isEstimateSubmit.value) {
      toast.info("이미 견적서를 등록했습니다.", {
        transition: toast.TRANSITIONS.SLIDE,
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    ModelStore().removeAnalysisRaw();

    axios
      .post("/estimates", pEstimate)
      .then((r) => {
        if (!r) {
          loaderButtonStore.stop();
          toast.error("네트워크가 연결이 안되있거나 서버에 연결할 수 없습니다.", {
            transition: toast.TRANSITIONS.SLIDE,
            position: toast.POSITION.TOP_CENTER,
          });
          return;
        }

        if (ResponseStatusCode.Create_Estimate__Not_Found_Option === r.data.status) {
          loaderButtonStore.stop();
          toast.error("배송비용 설정을 찾을 수 없습니다, 관리자에게 문의하세요.", {
            transition: toast.TRANSITIONS.SLIDE,
            position: toast.POSITION.TOP_CENTER,
          });
          return;
        }

        isEstimateSubmit.value = false;

        // 2024.08.20
        estimate.value.id = r.data.result.estimate.id;
        estimate.value.clientName = r.data.result.estimate.clientName;
        estimate.value.clientCompany = r.data.result.estimate.clientCompany;

        // toast.success("견적서를 등록했습니다, 담당자가 검토 후 연락드리겠습니다.", { autoClose: 5000, transition: toast.TRANSITIONS.SLIDE, position: toast.POSITION.TOP_CENTER });
        // CommonStore().closeModal();
        // const data: GModalData = {
        //   modalName: Modals.SubmiEstimate,
        //   title: "알림",
        //   content: `No.${estimate.id} 견적서를 등록했습니다,\n담당자가 검토 후 연락드리겠습니다.`,
        //   result: {},
        // };
        // CommonStore().showGlobalModal(data);
        CommonStore().closeModal();
        router.push({
          name: "client-estimate-submit",
        });
      })
      .catch((e) => {
        console.error(e);
        loaderButtonStore.stop();
      });

    return;
  }

  async function reqSave() {
    const modelStore = ModelStore();

    const values: AdminEstimateSubmit = {
      estimate: estimate.value,
      models: modelStore.models,
    };

    const res = await axios.put(`/admin/estimates/${estimate.value.id}`, values);
    if (res.status === 200) {
      return res;
    }

    return res;
  }

  function reqFinalSubmit() {
    axios
      .post(`/admin/estimates/${estimate.value.id}`, {
        estimate: estimate.value,
        models: ModelStore().models,
      })
      .then((r) => {
        if (!r) {
          toast.error("네트워크가 연결이 안되있거나 서버에 연결할 수 없습니다.", {
            transition: toast.TRANSITIONS.SLIDE,
            position: toast.POSITION.TOP_CENTER,
          });
          return;
        }

        isEstimateSubmit.value = false;

        let content = `No.${estimate.value.id} 견적서를 등록했습니다.`;
        if (estimate.value.isActive) {
          content = `No.${estimate.value.id} 견적서를 수정했습니다.`;
        }

        const data: GModalData = {
          modalName: Modals.SubmiEstimate,
          title: "알림",
          content: content,
          result: {},
        };
        CommonStore().showGlobalModal(data);

        loaderButtonStore.stop();
      })
      .catch((e) => {
        console.error(e);
        loaderButtonStore.stop();
        toast.error("오류가 발생했습니다.", {
          transition: toast.TRANSITIONS.SLIDE,
          position: toast.POSITION.TOP_CENTER,
        });
      });
  }

  function reqGetEstimate(estimateId: string) {
    modelStore.clear();
    axios
      .get(`/admin/estimates/${estimateId}`)
      .then((r) => {
        // 반응성을 보장하기 위해 Object.assign 사용
        Object.assign(estimate.value, r.data.estimate);
        estimate.value.buildCost = r.data.estimate.buildCost;

        for (let m = 0; m < r.data.models.length; m++) {
          r.data.models[m].isUploaded = true;
          modelStore.addModel(r.data.models[m]);
        }

        commonStore.setIsViewLoader(false);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  async function reqDelete() {
    return new Promise((resolve, reject) => {
      axios
        .delete(`/admin/estimates/${estimate.value.id}`)
        .then((r) => {
          loaderButtonStore.stop();
          resolve(true);
        })
        .catch((e) => {
          loaderButtonStore.stop();
          reject(true);
        });
    });
  }

  return {
    isEstimateSubmit,
    estimate,
    createEstimate,
    calculateTotalCost,
    reqCreateEstimate,
    reqSubmit,
    reqFinalSubmit,
    reqGetEstimate,
    reqDelete,
    reqSave,
  };
});
